import { Directive, Input } from '@angular/core';
import {
  AbstractControl,
  ValidatorFn,
  NG_VALIDATORS,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { formatNumber } from '@angular/common';
import { Utilities, DateUtilities, isFeatureEnabled } from '@approot/utilities';
const enableLegFormattedDecimals = isFeatureEnabled(
  'enableLegFormattedDecimals',
  false
);
@Directive({
  selector: '[inputValidator]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: InputValidatorDirective,
      multi: true
    }
  ]
})
export class InputValidatorDirective {
  @Input() public inputValidator: string[] | string;
  @Input() public required: boolean;
  @Input() public fieldName: string;
  private errorsRelatedTo: string[];

  public validate(control: AbstractControl): ValidationErrors | null {
    this.errorsRelatedTo = [];
    if (!this.inputValidator) {
      return null;
    }
    let validatorNames: string[];
    if (typeof this.inputValidator === 'string') {
      validatorNames = [this.inputValidator];
    } else {
      validatorNames = this.inputValidator;
    }
    let allowNegativeNumericShorthand = false;

    const validatorsValues = validatorNames
      .map((validatorName: string) => {
        if (
          control.value === Utilities.customText ||
          (!this.required && (control.value === '' || control.value === null))
        ) {
          // THIS IS A TEMP FIX, AS SETTING ERRORS TO NULL BREAKS WHEN A DIFFERENT COMPONENT IS ADDED
          // AS IT CARRIES OUT VALIDATION FOR PREVIOUS LEGS WHEN A DIFFERENT LEG COMPONENT IS ADDED,
          // RESULTING IN AN ExpressionChangedAfterItHasBeenCheckedError
          return null;
        }
        switch (validatorName) {
          case 'negativeInteger':
            let negVal = control.value;
            if (!negVal || Utilities.isNumberFormatted(negVal, true)) {
              return null;
            } else if (
              Utilities.isMillionShorthand(negVal, true) ||
              Utilities.isThousandShorthand(negVal, true)
            ) {
              control.setValue(
                Utilities.shortHandNumberConverter(negVal, true)
              );
              return null;
            } else {
              if (!!negVal && new RegExp('^-?[0-9,]+$').test(negVal)) {
                let pureNumber = isNaN(negVal)
                  ? Number(negVal.replace(/,/g, ''))
                  : +negVal;
                control.setValue(formatNumber(pureNumber, 'en-GB', '1.0-4'), {
                  emitEvent: false
                });

                return null;
              } else {
                return { invalidEntry: { value: control.value } };
              }
            }
          case 'decimalNumber':
            return this.isValid(new RegExp('^[0-9]+([.][0-9]{4})$'))(control);

          case 'positiveNumber': {
            // optional decimals
            const val = Utilities.formattedNumberToNumber(control.value);
            if (!isNaN(val) && val > 0) {
              return null;
            } else {
              this.errorsRelatedTo.push('positiveNumber');
              return { invalidEntry: { value: control.value } };
            }
          }
          case 'positiveOrNegativeNumber': {
            // optional decimals
            const val = Utilities.formattedNumberToNumber(control.value);
            if (!isNaN(val)) {
              return null;
            } else {
              this.errorsRelatedTo.push('positiveOrNegativeNumber');
              return { invalidEntry: { value: control.value } };
            }
          }

          // @ts-ignore
          case 'positiveOrZeroInteger':
            if (+control.value === 0) return null;
          // tslint:disable-next-line: no-switch-case-fall-through
          case 'positiveInteger':
            if (
              typeof control.value === 'string' &&
              control.value.includes('e')
            ) {
              return { invalidEntry: { value: control.value } };
            }
            return +control.value > 0 && Number.isInteger(+control.value)
              ? null
              : { invalidEntry: { value: control.value } };
          case 'dateShorthand':
            if (
              control.value &&
              (DateUtilities.isCorrectDateFormat(control.value) ||
                DateUtilities.isValidLag(control.value))
            ) {
              return null;
            }
            return { invalidEntry: { value: control.value } };
          case 'prettyDate':
            if (control.value && DateUtilities.isPrettyDate(control.value)) {
              return null;
            }
            return { invalidEntry: { value: control.value } };
          case 'dateShorthandSettlementOnly':
            if (DateUtilities.isCorrectDateFormat(control.value)) {
              let date = control.value;
              if (DateUtilities.isDateOfSlashFormat(date)) {
                date = DateUtilities.restructureDate(date);
              }
              const expiryDateControl = control.parent.controls['expiryDate'];
              if (expiryDateControl) {
                // expiryDate doesn't always exist
                const settlementDate = new Date(date);
                const expiryDate = new Date(expiryDateControl.value);
                if (expiryDateControl.valid && settlementDate >= expiryDate) {
                  return null;
                }
              }
            }
            return { invalidEntry: { value: control.value } };
          case 'strike':
            if (
              new RegExp('^(?:(s|f|dn)|(\\dd|\\d{2}d))$', 'i').test(
                control.value
              )
            ) {
              return null;
            }

            if (enableLegFormattedDecimals) {
              return formattedDecimal(false)(control);
            } else {
              // can be removed when enableLegFormattedDecimals is removed
              if (!isNaN(control.value)) {
                return null;
              }
              return { invalidEntry: { value: control.value } };
            }
          // @ts-ignore
          case 'formattedDecimal': {
            return formattedDecimal(true)(control);
          }
          // @ts-ignore
          case 'negativeNumericShorthand':
            allowNegativeNumericShorthand = true;
          // tslint:disable-next-line: no-switch-case-fall-through
          case 'numericShorthand':
            // Note validation occurs before base currency is added or removed from text box
            let value = Utilities.notionalCurrencyRemover(
              control.value
            ).toString();
            if (
              !value ||
              Utilities.isNumberFormatted(value, allowNegativeNumericShorthand)
            ) {
              return null;
            } else if (
              Utilities.isMillionShorthand(
                value,
                allowNegativeNumericShorthand
              ) ||
              Utilities.isThousandShorthand(
                value,
                allowNegativeNumericShorthand
              )
            ) {
              control.setValue(
                Utilities.shortHandNumberConverter(
                  value,
                  allowNegativeNumericShorthand
                )
              );
              return null;
            } else {
              const re = allowNegativeNumericShorthand
                ? new RegExp('^-?[0-9,]+([.][0-9]+)?$')
                : new RegExp('^[0-9,]+([.][0-9]+)?$');
              if (!!value && re.test(value)) {
                const decimals = value.split('.')[1];
                let pureNumber = isNaN(value)
                  ? Number(value.replace(/,/g, ''))
                  : +value;

                let formattedNumber = formatNumber(
                  pureNumber,
                  'en-GB',
                  '1.0-0'
                );
                if (decimals) {
                  formattedNumber = `${formattedNumber}.${decimals}`;
                }
                control.setValue(formattedNumber, {
                  emitEvent: false
                });

                return null;
              } else return { invalidEntry: { value: control.value } };
            }
          case 'numericDisplayAllowNegative':
            return this.validateNumericDisplay(control, true);
          case 'numericDisplay':
            return this.validateNumericDisplay(control, false);

          case 'dateshorthandvaluationAdjustmentAmortizationEndDate': {
            return this.validateEndDate(
              'valuationAdjustmentAmortizationStartDate',
              control
            );
          }

          case 'irEndDate': {
            const startDate =
              this.fieldName === 'xccySwapEndDate'
                ? 'xccySwapStartDate'
                : 'swapStartDate';
            return this.validateEndDate(startDate, control);
          }

          case 'tenor': {
            const value = control.value.toLowerCase();
            if (new RegExp('^[1-9]+(y|w|d|m|b)$').test(value)) {
              if (new RegExp('[A-Z]+').test(control.value)) {
                control.setValue(value);
              }
              return null;
            }
            return { invalidEntry: { value: control.value } };
          }

          case 'currency': {
            const value = control.value.toUpperCase();
            if (new RegExp('^[A-Z]{3}$').test(value)) {
              if (new RegExp('[a-z]+').test(control.value)) {
                control.setValue(control.value.toUpperCase());
              }
              return null;
            }
            return { invalidEntry: { value: control.value } };
          }

          case 'percentage': {
            if (/^\d+(\.\d+)?%$/.test(control.value)) {
              return null;
            }
            this.errorsRelatedTo.push('percentage');
            return { invalidEntry: { value: control.value } };
          }

          case 'pips': {
            if (/^\d+(\.\d+)?[\s]pips$/.test(control.value)) {
              return null;
            }
            return { invalidEntry: { value: control.value } };
          }

          case 'percentageAllowNegatives': {
            if (new RegExp('^-?\\d+(\\.\\d+)?%$').test(control.value)) {
              return null;
            }
            return { invalidEntry: { value: control.value } };
          }

          case 'hhmmss': {
            if (/^\d\d:\d\d(:\d\d)?$/.test(control.value)) {
              return null;
            }
            if (/^\d\d\d\d(\d\d)?$/.test(control.value)) {
              return null;
            }
            return { invalidEntry: { value: control.value } };
          }

          default:
            return null;
        }
      })
      .every(value => value == null);

    return validatorsValues
      ? null
      : {
          invalidEntry: {
            value: control.value,
            errorsRelatedTo: this.errorsRelatedTo
          }
        };
  }

  private validateNumericDisplay(
    control: AbstractControl,
    allowNegative: boolean
  ): ValidationErrors {
    const value = Utilities.notionalCurrencyRemover(control.value);
    if (!value || Utilities.isNumberFormatted(value, allowNegative)) {
      return null;
    } else {
      const re = allowNegative
        ? new RegExp('^-?[0-9,]+([.][0-9]+)?$')
        : new RegExp('^[0-9,]+([.][0-9]+)?$');

      if (!!value && re.test(value)) {
        control.setValue(
          formatNumber(parseFloat(value.replace(/,/g, '')), 'en-GB', '1.0-0'),
          { emitEvent: false }
        );
        return null;
      } else return { invalidEntry: { value: control.value } };
    }
  }

  public validateEndDate(
    startDate: string,
    control: AbstractControl
  ): ValidationErrors | null {
    // carries out dateshorthandvaluation and makes sure that valuationAdjustmentAmortizationEndDate is after
    // valuationAdjustmentAmortizationStartDate

    const startDateControl = control.parent.controls[startDate];
    if (!startDateControl.value) {
      return null;
    }

    if (DateUtilities.isCorrectDateFormat(control.value)) {
      let date = control.value;
      if (DateUtilities.isDateOfSlashFormat(date)) {
        date = DateUtilities.restructureDate(date);
      }
      if (startDateControl && startDateControl.value) {
        let valuationAdjustmentAmortizationStartDate;
        if (DateUtilities.isDateOfSlashFormat(startDateControl.value)) {
          valuationAdjustmentAmortizationStartDate =
            DateUtilities.restructureDate(startDateControl.value);
        } else {
          valuationAdjustmentAmortizationStartDate = startDateControl.value;
        }

        if (
          startDateControl.valid &&
          new Date(date) > new Date(valuationAdjustmentAmortizationStartDate)
        ) {
          return null;
        }
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  public isValid(regex: RegExp): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const match = regex.test(control.value);
      // return null if there is a match (ie valid entry)
      return match ? null : { invalidEntry: { value: control.value } };
    };
  }
}

export function formattedDecimal(allowNegative: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      !enableLegFormattedDecimals ||
      Utilities.isNumberFormatted(control.value, allowNegative)
    ) {
      return null;
    }

    const regex = allowNegative
      ? '^-?[0-9,]+([.][0-9]+)?$'
      : '^[0-9,]+([.][0-9]+)?$';

    if (new RegExp(regex).test(control.value)) {
      control.setValue(
        Utilities.roundNumberToDp(
          Utilities.cleanFormattedNumeric(control.value),
          control.value.toString().split('.')[1]?.length || 0,
          true
        ),
        {
          emitEvent: false
        }
      );
      return null;
    }

    if (
      !control.hasValidator(Validators.required) &&
      !Utilities.isNotBlank(control.value)
    ) {
      return null;
    }

    return { invalidEntry: { value: control.value } };
  };
}
